import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

export const checkDuplicateRegistration = createServerFn({ method: 'GET' })
  .validator(z.object({ idNumber: z.string() }))
  .handler(async ({ data }) => {
    const { רישומיםBoard } = await import('@api/BoardSDK');
    const board = new רישומיםBoard();
    const result = await board
      .items()
      .withColumns(['column_9'])
      .where({ column_9: { contains: data.idNumber } })
      .withPagination({ limit: 1 })
      .execute();

    const exactMatch = result.items.some((item) => item.column_9 === data.idNumber);
    return { exists: exactMatch };
  });

export const createRegistration = createServerFn({ method: 'POST' })
  .validator(
    z.object({
      firstName: z.string(),
      lastName: z.string(),
      idNumber: z.string(),
      phone: z.string(),
      email: z.string(),
      street: z.string(),
      houseNumber: z.number(),
      entrance: z.string().optional(),
      zipCode: z.string().optional(),
      show: z.string(),
    })
  )
  .handler(async ({ data }) => {
    const { רישומיםBoard, קופוניםBoard } = await import('@api/BoardSDK');

    // 1. Check for duplicate registration
    const regBoard = new רישומיםBoard();
    const existingCheck = await regBoard
      .items()
      .withColumns(['column_9'])
      .where({ column_9: { contains: data.idNumber } })
      .withPagination({ limit: 1 })
      .execute();

    const hasDuplicate = existingCheck.items.some((item) => item.column_9 === data.idNumber);
    if (hasDuplicate) {
      throw new Error('DUPLICATE_REGISTRATION');
    }

    // 2. Check coupon availability before proceeding
    const couponsBoard = new קופוניםBoard();
    const availableCoupons = await couponsBoard
      .items()
      .withColumns(['column_', 'column'])
      .where({ column: 'זמין' })
      .withPagination({ limit: 1 })
      .execute();

    if (availableCoupons.items.length === 0) {
      throw new Error('NO_COUPONS_AVAILABLE');
    }

    const coupon = availableCoupons.items[0];

    // 3. Create the registration
    const createPayload: Record<string, any> = {
      name: `${data.firstName} ${data.lastName}`,
      column_: data.firstName,
      column_1: data.lastName,
      column_9: data.idNumber,
      column: data.street,
      column_2: data.houseNumber,
      column_4: 1,
      column2: [data.show],
      column_6: 'ממתין לבדיקה',
      column_8: new Date(),
    };

    if (data.phone) {
      createPayload.column_10 = { phone: data.phone, country: 'IL' };
    }
    if (data.email) {
      createPayload.column_11 = { email: data.email, label: data.email };
    }
    if (data.entrance) {
      createPayload.column_3 = data.entrance;
    }
    if (data.zipCode) {
      createPayload.column1 = data.zipCode;
    }

    const newItem = await regBoard
      .item()
      .create(createPayload)
      .inGroup('topics')
      .execute();

    // 4. Assign the coupon and write registration info to it
    let couponCode: string | null = null;
    try {
      couponCode = coupon.column_;
      const registrationInfo = `${data.firstName} ${data.lastName} | ת.ז: ${data.idNumber} | טלפון: ${data.phone} | ${data.street} ${data.houseNumber}`;

      await couponsBoard
        .item(coupon.id)
        .update({
          name: `${data.firstName} ${data.lastName}`,
          column: 'הוקצה',
          column__: data.idNumber,
          column_1: new Date(),
          column_4: registrationInfo,
        })
        .execute();
    } catch (couponErr) {
      console.error('Coupon assignment failed (registration still saved):', couponErr);
    }

    return { success: true, itemId: newItem.id, couponCode };
  });

export const getRegistrations = createServerFn({ method: 'GET' })
  .validator(
    z
      .object({
        status: z.string().optional(),
        search: z.string().optional(),
        limit: z.number().optional(),
        cursor: z.string().optional(),
      })
      .optional()
  )
  .handler(async ({ data }) => {
    const { רישומיםBoard } = await import('@api/BoardSDK');
    const board = new רישומיםBoard();
    const limit = data?.limit || 25;

    let query = board
      .items()
      .withColumns([
        'column_', 'column_1', 'column_9', 'column_10', 'column_11',
        'column', 'column_2', 'column_3', 'column1',
        'column_4', 'column2', 'column_6', 'column_8',
        'column__', 'column_5', 'column_7',
      ])
      .orderBy({ column: 'createdAt', direction: 'desc' });

    if (data?.status) {
      query = query.where({ column_6: data.status });
    }

    if (data?.search) {
      query = query.where({ column_9: { contains: data.search } });
    }

    query = query.withPagination({ limit, cursor: data?.cursor });

    const result = await query.execute();
    return { items: result.items, cursor: result.cursor };
  });

export const updateRegistrationStatus = createServerFn({ method: 'POST' })
  .validator(
    z.object({
      itemId: z.string(),
      status: z.enum(['ממתין לבדיקה', 'אושר', 'נדחה', 'נשלח SMS']),
      notes: z.string().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { רישומיםBoard } = await import('@api/BoardSDK');
    const board = new רישומיםBoard();

    const updatePayload: Record<string, any> = { column_6: data.status };
    if (data.notes) {
      updatePayload.column_7 = data.notes;
    }

    await board.item(data.itemId).update(updatePayload).execute();
    return { success: true };
  });

export const checkResidentStatus = createServerFn({ method: 'GET' })
  .validator(z.object({ idNumber: z.string() }))
  .handler(async ({ data }) => {
    const { תושביםמאושריםBoard } = await import('@api/BoardSDK');
    const board = new תושביםמאושריםBoard();

    const result = await board
      .items()
      .withColumns(['column_', 'column_2'])
      .where({ column_: { contains: data.idNumber } })
      .withPagination({ limit: 1 })
      .execute();

    const match = result.items.find((item) => item.column_ === data.idNumber);
    return {
      isResident: !!match,
      isActive: match?.column_2 === 'פעיל',
    };
  });

export const getStatusCounts = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async () => {
    const { רישומיםBoard } = await import('@api/BoardSDK');
    const board = new רישומיםBoard();

    const result = await board
      .aggregate()
      .groupBy('column_6')
      .countItems('count')
      .execute();

    return result || [];
  });
